import { Controller } from "@hotwired/stimulus";

const OPEN_START_EVENT = "modal:open:start";
const OPEN_END_EVENT = "modal:open:end";
const CLOSE_START_EVENT = "modal:close:start";
const CLOSE_END_EVENT = "modal:close:end";

export default class extends Controller {
  static classes = [
    "backdropEnter",
    "backdropHidden",
    "backdropLeave",
    "backdropVisible",
    "modalEnter",
    "modalHidden",
    "modalLeave",
    "modalVisible",
  ];

  static targets = ["container", "modal", "backdrop"];

  static values = {
    openDuration: Number,
    closeDuration: Number,
    openOnConnect: Boolean,
    dismissable: Boolean,
  };

  connect() {
    this.timeoutIds = [];
    this.closing = false;
    this.selfClosing = false;

    this.handleBeforeCache = () => {
      this.clearTimeouts();
      this.closing = false;
      this.closeDialog();
    };
    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    this.closeDialog();

    this.resetToClosed();

    if (this.openOnConnectValue) this.open();
  }

  resetToClosed() {
    this.backdropTarget.classList.remove(...this.backdropLeaveClasses);
    this.backdropTarget.classList.remove(...this.backdropVisibleClasses);
    this.modalTarget.classList.remove(...this.modalLeaveClasses);
    this.modalTarget.classList.remove(...this.modalVisibleClasses);

    this.backdropTarget.classList.add(...this.backdropEnterClasses);
    this.backdropTarget.classList.add(...this.backdropHiddenClasses);
    this.modalTarget.classList.add(...this.modalEnterClasses);
    this.modalTarget.classList.add(...this.modalHiddenClasses);
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
    this.clearTimeouts();
  }

  cancel(event) {
    event.preventDefault();
    this.dismiss();
  }

  dismiss() {
    if (!this.dismissableValue) return;

    this.close();
  }

  closeDialog() {
    if (!this.containerTarget.open) return;

    this.selfClosing = true;
    this.containerTarget.close();
  }

  closed() {
    const browserClosedIt = !this.selfClosing;
    this.selfClosing = false;
    this.closing = false;

    if (!browserClosedIt) return;

    this.clearTimeouts();
    this.resetToClosed();

    if (!this.dismissableValue) this.open();
  }

  open() {
    this.abortClose();
    this.clearTimeouts();

    if (!this.containerTarget.open) this.containerTarget.showModal();
    this.dispatchEvent(OPEN_START_EVENT);

    this.later(100, () => {
      this.backdropTarget.classList.add(...this.backdropVisibleClasses);
      this.backdropTarget.classList.remove(...this.backdropHiddenClasses);
      this.modalTarget.classList.add(...this.modalVisibleClasses);
      this.modalTarget.classList.remove(...this.modalHiddenClasses);

      this.later(this.openDurationValue, () => {
        this.backdropTarget.classList.add(...this.backdropLeaveClasses);
        this.backdropTarget.classList.remove(...this.backdropEnterClasses);
        this.modalTarget.classList.add(...this.modalLeaveClasses);
        this.modalTarget.classList.remove(...this.modalEnterClasses);
        this.dispatchEvent(OPEN_END_EVENT);
      });
    });
  }

  abortClose() {
    if (!this.closing) return;

    this.closing = false;
    this.dispatchEvent(CLOSE_END_EVENT);
  }

  close() {
    if (!this.containerTarget.open || this.closing) return;

    this.clearTimeouts();
    this.closing = true;
    this.dispatchEvent(CLOSE_START_EVENT);
    this.backdropTarget.classList.add(...this.backdropHiddenClasses);
    this.backdropTarget.classList.remove(...this.backdropVisibleClasses);
    this.modalTarget.classList.add(...this.modalHiddenClasses);
    this.modalTarget.classList.remove(...this.modalVisibleClasses);

    this.later(this.closeDurationValue, () => {
      this.backdropTarget.classList.add(...this.backdropEnterClasses);
      this.backdropTarget.classList.remove(...this.backdropLeaveClasses);
      this.modalTarget.classList.add(...this.modalEnterClasses);
      this.modalTarget.classList.remove(...this.modalLeaveClasses);
      this.closeDialog();
      this.closing = false;
      this.dispatchEvent(CLOSE_END_EVENT);
    });
  }

  later(delay, callback) {
    const id = setTimeout(() => {
      this.timeoutIds = this.timeoutIds.filter((timeoutId) => timeoutId !== id);
      callback();
    }, delay);

    this.timeoutIds.push(id);
  }

  clearTimeouts() {
    this.timeoutIds.forEach(clearTimeout);
    this.timeoutIds = [];
  }

  dispatchEvent(eventName) {
    const event = new CustomEvent(eventName);
    this.element.dispatchEvent(event);
  }
}
